import { COUNTRY_CURRENCIES } from "@redotech/money/countries";
import { CountryCurrency } from "@redotech/money/country";
import { useTriggerLoad } from "@redotech/react-util/load";
import { CURRENCY_FORMAT } from "@redotech/redo-web/currency";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { getCurrencyExchangeRates } from "../api";
import { UserContext } from "../app/user";

const DEFAULT_CURRENCY: CountryCurrency = COUNTRY_CURRENCIES.getByCountry("US");

export const CurrencyContext = createContext<CurrencyEnv>({
  currency: DEFAULT_CURRENCY,
  formatCurrency: String,
  setCurrency() {},
});

export interface CurrencyEnv {
  currency: CountryCurrency;
  formatCurrency(amount: number): string;
  setCurrency(currency: CountryCurrency): void;
}

export const CurrencyProvider = ({ children }) => {
  const user = useContext(UserContext);
  const [currency, setCurrency] = useState<CountryCurrency>(DEFAULT_CURRENCY);

  const [exchangeRatesLoad, loadExchangeRates] = useTriggerLoad(async () => {
    try {
      const response = await getCurrencyExchangeRates();
      return response.data;
    } catch (e) {
      console.error(e);
    }
  });

  // a bit convoluted, but re-requests in case of error
  useEffect(() => {
    if (
      currency.currencyCode !== DEFAULT_CURRENCY.currencyCode &&
      !exchangeRatesLoad.pending &&
      !exchangeRatesLoad.value
    ) {
      loadExchangeRates();
    }
  }, [currency]);

  const formatCurrency = (amount: number) => {
    let code: string | undefined;
    if (exchangeRatesLoad.value) {
      amount *= exchangeRatesLoad.value[currency.currencyCode];
      code = currency.currencyCode;
    }
    return CURRENCY_FORMAT(code).format(amount);
  };

  useEffect(() => {
    if (user?.address) {
      const countryCode = JSON.parse(user.address)?.country;
      // Finds the currency code we should default to use for the user based on their address
      const currency = COUNTRY_CURRENCIES.getByCountry(countryCode);
      setCurrency(currency || DEFAULT_CURRENCY);
    }
  }, [user]);

  const value: CurrencyEnv = useMemo(() => {
    return {
      currency,
      formatCurrency,
      setCurrency,
    };
  }, [currency, exchangeRatesLoad.value]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
